//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import classnames from 'classnames';
import queryString from 'query-string';
import compose from 'recompose/compose';
import { translate } from 'react-i18next';

import ZoneMapActions from 'common/redux/actions/zoneMap';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import ZoneMapModalContainer from 'common/entity/mapMaster/ZoneMapModalContainer';
import { Route, Link } from 'react-router-dom';

import DataUtils from 'common/utils/DataUtils';
import VisitUtils from 'common/utils/VisitUtils';

import { fetchHomebasesByClientId } from 'common/redux/actions/homebase';
import { personnelActiveTeamValuesSelector } from 'common/redux/selectors/personnelTeamsSelector';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    editEntityIdSelector,
    teamIdQuerySelector,
    homebaseIdQuerySelector
} from 'common/redux/selectors/queryParamsSelectors';
import ZoneMapSelectors from 'common/redux/selectors/zoneMap';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';

import {
    paginatedTableWithControlsFactory,
    PaginatedTableWithExpander
} from 'common/components/core/table';
import { withRouter } from 'common/components/core/router';
import { withUnitConverter } from 'common/containers/intl';

import theme from 'common/entity/BulkEditContainer.scss';
import customTheme from './ZoneMapMasterContainer.scss';

import { getHomebasesIfPresent } from 'common/redux/selectors/homebase';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import MapVersionsTable from 'common/entity/mapMaster/MapVersionsTable';
import TeamList from 'common/components/core/common/TeamList';
import { List, Map } from 'immutable';

import { CellDataTypes } from 'common/components/core/table/CellDataTypes';
import { DeleteConfirmationDialog } from 'common/entity/generic/DeleteConfirmationDialog';
import TableControls from 'common/components/core/table/TableControls';
import ZoneMapUsecaseDropdown from 'common/entity/mapMaster/ZoneMapUsecaseDropdown';
import ZoneMapUsecaseLabel from 'common/entity/mapMaster/ZoneMapUsecaseLabel';
import type { RouterHistory } from 'react-router-dom';
import type { Location } from 'common/components/core/router';
import type { VersionedZoneMap } from 'common/models/internal/VersionedZoneMap';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { Homebase } from 'common/models/internal/Homebase';
import type { TFunction } from 'react-i18next';

import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import { ConnectedTeamSelectorFilter } from 'common/entity/components/TeamSelectorFilter';
import mapMasterTestIds from 'common/tests/masterManagement/mapMaster.json';

const ENTITY_NAME_ID = 'zoneMapMaster';
const ENTITY_TABLE_ID = 'ZoneMapMaster';

const ZoneMapControls = ({
    row,
    tdProps: {
        rest: { activeUsecases }
    },
    t,
    ...others
}) => {
    let deleteDisabled = false;
    if (activeUsecases && activeUsecases.length) {
        deleteDisabled = true;
    }
    return (
        <TableControls
            {...others}
            row={row}
            disableDeleteButton={deleteDisabled}
            deleteButtonTooltip={
                deleteDisabled ? t('zoneMap.disableActiveDelete') : null
            }
        />
    );
};
const TranslatedZoneMapControls = translate('entityManagement')(
    ZoneMapControls
);
const PaginatedTableWithControlsAndExpander = paginatedTableWithControlsFactory(
    {
        PaginatedTableComponent: PaginatedTableWithExpander,
        TableControlsComponent: TranslatedZoneMapControls
    }
);

const UsecaseDropdown = ({
    activeVersion,
    mapId,
    activeUsecases,
    changeActiveUsecases,
    usecases,
    theme
}) => {
    return (
        <ZoneMapUsecaseDropdown
            theme={theme}
            activeVersion={activeVersion}
            mapId={mapId}
            activeUsecases={activeUsecases}
            usecases={usecases}
            changeActiveUsecases={changeActiveUsecases}
        />
    );
};

const COLUMNS = [
    {
        headerTextId: 'zoneMap.columnData.name',
        id: 'name',
        accessor: 'map.name',
        property: 'name',
        infixSearchable: true
    },
    {
        headerTextId: 'zoneMap.columnData.activeLink',
        id: 'activeLink',
        sortable: false,
        filterable: false,
        Cell: props => {
            const { original: zoneMap } = props;
            if (DataUtils.isEmpty(zoneMap.activeVersion)) {
                return null;
            }
            return (
                <Link
                    className={classnames(theme.cellText, theme.link)}
                    to={`/client/${zoneMap.map.clientId}/entity/map/${zoneMap.map.id}/active`}
                >
                    v{zoneMap.activeVersion}
                </Link>
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.latestVersion',
        accessor: 'map.version',
        sortable: false,
        filterable: false,
        Cell: props => {
            const {
                original: zoneMap,
                tdProps: {
                    rest: { theme }
                }
            } = props;
            return (
                <Link
                    title={zoneMap.currentVersion}
                    className={classnames(theme.cellText, theme.link)}
                    to={`/client/${zoneMap.map.clientId}/entity/map/${zoneMap.map.id}`}
                >
                    v{zoneMap.map.version}
                </Link>
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.status',
        id: 'status',
        sortable: false,
        filterable: false,
        noCellPadding: true,
        Cell: props => {
            const {
                tdProps: {
                    rest: {
                        usecases,
                        activeUsecases,
                        changeActiveUsecases,
                        mapId,
                        activeVersion,
                        theme
                    }
                }
            } = props;
            return (
                <div className={theme.statusCell}>
                    <UsecaseDropdown
                        theme={theme}
                        activeVersion={activeVersion}
                        mapId={mapId}
                        usecases={usecases}
                        activeUsecases={activeUsecases}
                        changeActiveUsecases={changeActiveUsecases}
                    />
                    {activeUsecases && !!activeUsecases.length ? (
                        <ZoneMapUsecaseLabel usecase={[activeUsecases]} />
                    ) : null}
                </div>
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.homebase',
        id: 'homebase',
        sortable: false,
        filterable: false,
        property: 'homebaseId.locationId',
        Cell: props => {
            const {
                original: zoneMap,
                tdProps: {
                    rest: { homebases }
                }
            } = props;
            const {
                map: {
                    homebaseId: { locationId }
                }
            } = zoneMap;
            const homebase =
                homebases &&
                homebases.find(homebase => homebase.id === locationId);
            return homebase && homebase.name ? (
                <span title={locationId} className={theme.cellText}>
                    {homebase.name}
                </span>
            ) : null;
        }
    },
    {
        headerTextId: 'zoneMap.columnData.teams',
        id: 'teams',
        sortable: false,
        property: 'teamId.teamId',
        FilterComponent: ConnectedTeamSelectorFilter,
        getExtraFilterProps: () => ({
            isMulti: false,
            disableBlanksSelectOption: true,
            showSelectFooter: false,
            submitOnOutsideClick: true,
            testId: mapMasterTestIds.table.teamFilterCell
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values, allowEmptyOrNull }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('teamId.teamId')
                .withOperation(FilterOperation.StartsWith)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull)
                .withCaseSensitive(true);
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = List([(original: VersionedZoneMap).map.teamId]);
            return (
                <TeamList
                    disableObserver
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                />
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: zoneMap,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                zoneMap?.map?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.updatedOn',
        accessor: 'map.auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'zoneMap.columnData.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: zoneMap,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                zoneMap?.map?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.createdOn',
        accessor: 'map.auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type ZoneMapsDataType = {
    maps: Array<VersionedZoneMap>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    fetchZoneMaps: ({ clientId: string }, Object) => void,
    clearData: () => void,
    disableEntity: ({ clientId: string, mapId: string }) => Promise<*>,
    data: ZoneMapsDataType,
    isAddZoneMapActive: boolean,
    entityIdToEdit: ?string,
    isLoading: boolean,
    isDeleteDialogLoading: boolean,
    location: Location,
    history: RouterHistory,
    unitConverter: UnitConversionHelper,
    entitySettings: EntitySettings,
    tableSettings: ?Array<Object>,
    homebaseId?: string,
    teamId?: string,
    homebases: List<Homebase>,
    isFetchingHomebases?: boolean,
    saveTableSettings: (string, Array<Object>) => void,
    t: TFunction
};

type State = {
    isDeleteDialogActive: boolean,
    selectedMap?: ?VersionedZoneMap,
    expandedRows: Map<number, boolean>
};

const ZoneMapsMaster = entityMasterFactory({
    PaginatedTableComponent: PaginatedTableWithControlsAndExpander,
    COLUMNS,
    options: {
        entityTableId: ENTITY_TABLE_ID
    }
});

class ZoneMapMasterContainer extends PureComponent<Props, State> {
    masterContainer: React.ElementRef<any>;
    state = {
        isDeleteDialogActive: false,
        isDeleteDialogLoading: false,
        selectedMap: null,
        expandedRows: Map()
    };

    UNSAFE_componentWillMount = () => {
        this.fetchOtherResources();
    };

    componentDidUpdate = (prevProps: Props) => {
        const { clientId } = this.props;
        if (clientId !== prevProps.clientId) {
            this.fetchOtherResources();
        }
    };

    fetchOtherResources = () => {
        this.fetchHomebases();
        this.fetchVerifyAccess();
    };

    fetchHomebases = () => {
        const { clientId, fetchHomebasesByClientId } = this.props;
        fetchHomebasesByClientId(clientId);
    };

    fetchVerifyAccess = () => {
        const { clientId, fetchVerifyAccess } = this.props;
        fetchVerifyAccess({ clientId });
    };

    fetchUsecasesMap = () => {
        const { clientId, fetchUsecasesMap } = this.props;
        fetchUsecasesMap({ clientId });
    };

    changeActiveUsecases = ({ mapId }, usecases) => {
        const { clientId, changeActiveUsecases } = this.props;
        changeActiveUsecases({ clientId, mapId }, usecases, {
            onSuccess: this.fetchUsecasesMap
        });
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }

        const {
            teams,
            entitySettings,
            usecasesMap,
            theme,
            homebases,
            isGlobalPersonnel
        } = this.props;
        const {
            original: {
                map: { id },
                activeVersion
            }
        } = rowInfo;
        const activeUsecases = (usecasesMap && usecasesMap.get(id)) || [];
        const { mapSettings } = entitySettings || {};
        const usecases = mapSettings
            ? mapSettings.usecases
                  .map(usecase => ({
                      label: usecase,
                      value: usecase
                  }))
                  .toArray()
            : [];

        return {
            homebases,
            activeUsecases,
            activeVersion,
            mapId: id,
            teams,
            theme,
            usecases,
            changeActiveUsecases: this.changeActiveUsecases,
            isGlobalPersonnel
        };
    };

    renderSubTable = row => {
        const {
            theme,
            t,
            entitySettings: {
                mapSettings: { mapMetadataSettings }
            }
        } = this.props;
        const { original } = row;
        const { activeVersion } = original;
        return (
            <div className={theme.subTable}>
                <MapVersionsTable
                    row={row}
                    mapId={row.original.map.id}
                    activeVersion={activeVersion}
                    t={t}
                    mapMetadataSettings={mapMetadataSettings}
                />
            </div>
        );
    };

    onCancelCreateMap = () => {
        const { match, history } = this.props;
        history.push(match.url);
    };

    goToCreateMap = ({ teamId, homebaseId }) => {
        const { match, history } = this.props;
        let query = {
            add: null
        };
        if (teamId) {
            query = { ...query, teamId };
        }
        if (homebaseId) {
            query = {
                ...query,
                homebaseId
            };
        }
        var path = {
            pathname: match.url,
            search: queryString.stringify(query)
        };
        history.push(path);
    };

    getHomebaseLatLng = (homebaseId?: ?string, homebases: List<Homebase>) => {
        const homebase =
            homebaseId &&
            homebases &&
            homebases.find(homebase => homebase.id === homebaseId);
        const mapCenter =
            homebase && homebase.geometry && homebase.geometry.latLng
                ? homebase.geometry.latLng.flatten()
                : undefined;
        return mapCenter;
    };

    renderFullMap = routerProps => {
        const {
            isAddZoneMapActive,
            homebaseId,
            teamId,
            homebases,
            clientId,
            entitySettings: {
                mapSettings: { mapMetadataSettings }
            },
            t
        } = this.props;
        const { match } = routerProps;
        const isValidMapId = typeof match.params.mapId !== 'undefined';
        return isValidMapId || isAddZoneMapActive ? (
            <ZoneMapModalContainer
                {...routerProps}
                t={t}
                clientId={clientId}
                isAddZoneMapActive={isAddZoneMapActive}
                homebaseId={homebaseId}
                teamId={teamId}
                mapCenter={this.getHomebaseLatLng(homebaseId, homebases)}
                goToCreateMap={this.goToCreateMap}
                onCancelCreateMap={this.onCancelCreateMap}
                onBack={this.refreshMapListData}
                mapMetadataSettings={mapMetadataSettings}
            />
        ) : null;
    };

    refreshMapListData = (): void => {
        this.masterContainer.reloadData();
    };

    getAdditionalTableProps = () => {
        const { usecasesMap } = this.props;
        return {
            usecasesMap,
            onDelete: this.handleDeleteClick
        };
    };

    onDeleteSuccess = () => {
        const { selectedMap } = this.state;
        const expandedRows = this.removeDeletedRow(selectedMap);
        this.setState({
            isDeleteDialogActive: false,
            selectedMap: null,
            expandedRows
        });
    };

    onCancelDelete = () => {
        this.setState({ isDeleteDialogActive: false, selectedMap: null });
    };

    handleDeleteClick = row => {
        this.setState({ isDeleteDialogActive: true, selectedMap: row });
    };

    //TODO - Move to HOC
    /*
        Given a row to be deleted, returns a set of expanded rows post deletion.
        Removes deletedRowIndex from expanded rows and for rows after deleted
        row, reduces the index by 1.
    */
    removeDeletedRow = row => {
        const { expandedRows } = this.state;
        const {
            data: { entities }
        } = this.props;
        const {
            map: { id }
        } = row;
        const deletedRowIndex = entities.findIndex(map => map.map.id === id);
        const newExpandedRows = expandedRows
            .delete(deletedRowIndex)
            .mapKeys(rowIndex => {
                return rowIndex > deletedRowIndex ? rowIndex - 1 : rowIndex;
            });
        return newExpandedRows;
    };

    handleRowExpansion = (newExpanded, [index]) => {
        const { expandedRows } = this.state;
        const isRowExpanded = newExpanded[index] ? true : false;
        this.setState({
            expandedRows: expandedRows.set(index, isRowExpanded)
        });
    };

    resetExpandedRows = () => {
        const { expandedRows } = this.state;
        this.setState({ expandedRows: expandedRows.clear() });
    };

    renderChildren = (
        t,
        match,
        selectedIds,
        deleteInnerText,
        isDeleteDialogActive,
        isDeleteDialogLoading
    ) => {
        const deleteTitle = t('zoneMap.deleteMap');
        return ({ onDelete }) => {
            return (
                <div>
                    <Route
                        path={`${match.path}/:mapId?/:versionStatus?/:versionNumber?`}
                        render={this.renderFullMap}
                    />
                    {isDeleteDialogActive ? (
                        <DeleteConfirmationDialog
                            active={isDeleteDialogActive}
                            selectedRows={selectedIds}
                            onDelete={onDelete}
                            onCancel={this.onCancelDelete}
                            isLoading={isDeleteDialogLoading}
                            innerText={deleteInnerText}
                            title={deleteTitle}
                        />
                    ) : null}
                </div>
            );
        };
    };

    getMasterContainerRef = (ref: React.ElementRef<any>): void => {
        this.masterContainer = ref;
    };

    render() {
        const {
            clientId,
            match,
            t,
            isLoading,
            isDeleteDialogLoading
        } = this.props;
        const { isDeleteDialogActive, selectedMap, expandedRows } = this.state;
        let mapName = '';
        let deleteInnerText = '';
        let selectedIds = [];

        if (selectedMap) {
            mapName = selectedMap.map.name;
            deleteInnerText = t('zoneMap.mapDeleteWarning', {
                mapName
            });
            selectedIds = [selectedMap.map.id];
        }
        return (
            <ZoneMapsMaster
                {...this.props}
                refProvider={this.getMasterContainerRef}
                getAdditionalTableProps={this.getAdditionalTableProps}
                emptyStateImageSrc="/img/zone-maps-master-empty.png"
                entityNameId={ENTITY_NAME_ID}
                entityId={'mapId'}
                entitySettingsName={'mapSettings'}
                getTdProps={this.getTdProps}
                fetchOptions={{ clientId }}
                fetchBulkData={this.fetchUsecasesMap}
                SubComponent={this.renderSubTable}
                selectedRows={selectedIds}
                onDeleteSuccess={this.onDeleteSuccess}
                expanded={expandedRows.toJS()}
                onExpandedChange={this.handleRowExpansion}
                onSortedChange={this.resetExpandedRows}
                onFilteredChange={this.resetExpandedRows}
                onPageChange={this.resetExpandedRows}
                onPageSizeChange={this.resetExpandedRows}
            >
                {this.renderChildren(
                    t,
                    match,
                    selectedIds,
                    deleteInnerText,
                    isDeleteDialogActive,
                    isDeleteDialogLoading,
                    isLoading
                )}
            </ZoneMapsMaster>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isLoading = ZoneMapSelectors.isEntitiesLoading(state, ownProps);
    const data = ZoneMapSelectors.getEntities(state, ownProps);
    const usecasesMap = ZoneMapSelectors.getUsecasesMap(state, ownProps);
    const isAddZoneMapActive = isAddEntityActiveSelector(state, ownProps);
    const entityIdToEdit = editEntityIdSelector(state, ownProps);
    const entitySettings = getEntitySettings(state, ownProps);
    const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
    const teams = teamsSelector(state, ownProps);
    const homebases = getHomebasesIfPresent(state, ownProps);
    const teamId = teamIdQuerySelector(state, ownProps);
    const homebaseId = homebaseIdQuerySelector(state, ownProps);
    const activeTeams = personnelActiveTeamValuesSelector(state, ownProps);
    const isDeleteDialogLoading = ZoneMapSelectors.isDeletingEntity(
        state,
        ownProps
    );
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

    return {
        clientId,
        isLoading,
        data,
        usecasesMap,
        isAddZoneMapActive,
        entityIdToEdit,
        entitySettings,
        tableSettings,
        teams,
        homebases,
        teamId,
        homebaseId,
        activeTeams,
        isDeleteDialogLoading,
        isGlobalPersonnel
    };
};

const withRedux = connect(mapStateToProps, {
    fetchEntities: ZoneMapActions.fetchActiveEntities,
    fetchUsecasesMap: ZoneMapActions.fetchUsecasesMap,
    changeActiveUsecases: ZoneMapActions.changeActiveUsecases,
    disableEntity: ZoneMapActions.disableEntity,
    clearData: ZoneMapActions.clearData,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
    fetchHomebasesByClientId,
    fetchVerifyAccess: ZoneMapActions.fetchVerifyAccess
});

const enhance = compose(
    themr('BulkEditContainer', theme),
    themr('ZoneMapMasterContainer', customTheme),
    withRouter,
    withUnitConverter,
    withRedux,
    translate('entityManagement'),
    MasterContainerHOC
);

const ThemedZoneMapMasterContainer = enhance(ZoneMapMasterContainer);
export default ThemedZoneMapMasterContainer;
